import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { TrendUp, Megaphone, CursorClick, UserPlus, Percent, ArrowSquareOut, CurrencyGbp, ArrowsClockwise, ArrowCounterClockwise, Scales } from "@phosphor-icons/react";

const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
const CH_COLOUR = { linkedin: "#0A66C2", facebook: "#1877F2", instagram: "#E1306C", x: "#0E103F" };

function Stat({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="card-surface p-4" data-testid={`launch-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}1a` }}>
          <Icon size={14} weight="fill" style={{ color }} />
        </span>
        <span className="overline text-navy/65 truncate">{label}</span>
      </div>
      <div className="font-heading font-black text-2xl text-navy tracking-tight">{value}</div>
      {sub && <div className="text-[0.7rem] text-navy/65 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function AdminLaunch() {
  const [camp, setCamp] = useState(null);
  const [trend, setTrend] = useState(null);
  const [stats, setStats] = useState(null);
  const [compare, setCompare] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [emailDraft, setEmailDraft] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  const loadCompare = () =>
    api.get("/admin/compare").then(({ data }) => { setCompare(data); setEmailDraft(data.notify_email || ""); }).catch(() => {});

  useEffect(() => {
    api.get("/admin/learning/campaign/analytics").then(({ data }) => setCamp(data)).catch(() => setCamp({ posts: [], channels: [] }));
    api.get("/admin/stats/trend?days=90").then(({ data }) => setTrend(data.snapshots || [])).catch(() => setTrend([]));
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    loadCompare();
  }, []);

  const refreshCompare = async () => {
    setRefreshing(true);
    try {
      await api.post("/admin/compare/refresh", { notify: true });
      toast.success("Checking competitors — the page updates automatically and you'll get an email if anything changed.");
      setTimeout(loadCompare, 60000);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start the check"); }
    finally { setRefreshing(false); }
  };
  const revertCompare = async () => {
    if (!window.confirm("Revert the Compare page to the built-in default competitor data?")) return;
    try { await api.post("/admin/compare/reset"); toast.success("Compare page reset to defaults"); loadCompare(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not revert"); }
  };
  const toggleApproval = async (require_approval) => {
    try {
      await api.put("/admin/compare/settings", { require_approval });
      setCompare((c) => ({ ...(c || {}), require_approval }));
      toast.success(require_approval ? "Changes will now wait for your approval" : "Changes will publish automatically");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const approvePending = async () => {
    try { const { data } = await api.post("/admin/compare/approve"); toast.success(`Published ${data.published} change${data.published === 1 ? "" : "s"}`); loadCompare(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); }
  };
  const discardPending = async () => {
    if (!window.confirm("Discard the pending competitor changes?")) return;
    try { await api.post("/admin/compare/discard"); toast.success("Pending changes discarded"); loadCompare(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not discard"); }
  };
  const saveNotifyEmail = async () => {
    setSavingEmail(true);
    try {
      const { data } = await api.put("/admin/compare/notify-email", { notify_email: emailDraft.trim() });
      setCompare((c) => ({ ...(c || {}), notify_email: data.notify_email }));
      toast.success(`Monthly summary will be emailed to ${data.notify_email}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the email address"); }
    finally { setSavingEmail(false); }
  };

  const chartData = (trend || []).map((r) => ({ date: r.date?.slice(5), mrr: r.mrr, active: r.active }));
  const topPosts = (camp?.posts || []).filter((p) => p.visits > 0 || p.signups > 0 || p.revenue > 0).slice(0, 12);

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="overline text-navy/65 mb-1">Platform</div>
        <h1 className="font-heading font-black text-navy text-3xl tracking-tight">Launch analytics</h1>
        <p className="text-navy/70 text-sm mt-1">Which posts drive sign-ups — and how they move the needle on revenue.</p>
      </div>

      {!camp || !stats ? (
        <div className="py-20 text-center text-navy/65 font-mono text-sm tracking-widest">LOADING…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <Stat icon={CursorClick} label="Tracked visits" value={(camp.total_visits || 0).toLocaleString("en-GB")} sub="from social links" color="#F37A24" />
            <Stat icon={UserPlus} label="Attributed signups" value={camp.total_attributed || 0} sub={`of ${camp.total_signups || 0} total`} color="#00A859" />
            <Stat icon={CurrencyGbp} label="Revenue attributed" value={gbp(camp.total_revenue)} sub={`${camp.total_paying || 0} paying from social`} color="#00A859" />
            <Stat icon={Percent} label="Visit → Signup" value={`${camp.total_visits ? Math.round((camp.total_attributed / camp.total_visits) * 100) : 0}%`} sub="social conversion" color="#0E103F" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left: social funnel */}
            <div>
              <h2 className="overline text-navy/65 mb-2.5 flex items-center gap-1.5"><Megaphone size={14} weight="fill" /> Campaign funnel</h2>

              {(camp.channels || []).length > 0 && (
                <div className="card-surface p-4 mb-3" data-testid="launch-channels">
                  <div className="text-sm font-semibold text-navy mb-3">By channel</div>
                  <div className="space-y-2.5">
                    {camp.channels.map((c) => (
                      <div key={c.channel} className="flex items-center gap-3" data-testid={`launch-channel-${c.channel}`}>
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: CH_COLOUR[c.channel] || "#64748B" }} />
                        <span className="text-sm font-medium text-navy w-20 shrink-0">{c.label}</span>
                        <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${Math.min(100, c.conversion)}%`, background: CH_COLOUR[c.channel] || "#64748B" }} />
                        </div>
                        <span className="text-xs text-navy/70 w-24 text-right shrink-0">{c.visits} → {c.signups} · {c.conversion}%</span>
                        <span className="text-sm font-heading font-bold w-16 text-right shrink-0" style={{ color: c.revenue > 0 ? "#00A859" : "#0E103F" }} data-testid={`launch-channel-revenue-${c.channel}`}>
                          {c.revenue > 0 ? gbp(c.revenue) : "£0"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="card-surface overflow-hidden" data-testid="launch-posts">
                <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-navy">Top posts by revenue</div>
                {topPosts.length === 0 ? (
                  <div className="p-8 text-center text-navy/60 text-sm">No tracked visits yet — sign-ups will attribute here as your posts go live.</div>
                ) : (
                  <div className="divide-y divide-slate-50 max-h-[420px] overflow-y-auto">
                    {topPosts.map((p) => (
                      <div key={p.id} className="px-4 py-3 flex items-center gap-3" data-testid={`launch-post-${p.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-navy truncate">{p.headline}</div>
                          <div className="text-[0.7rem] text-navy/55 flex items-center gap-2">
                            <span className="capitalize">{(p.theme || "").replace(/_/g, " ")}</span>
                            {p.scheduled_day && <span>· {p.scheduled_day}</span>}
                            <a href={p.tracked_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-orange hover:underline" style={{ color: "#F37A24" }}>
                              link <ArrowSquareOut size={10} weight="bold" />
                            </a>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-sm font-heading font-bold" style={{ color: p.revenue > 0 ? "#00A859" : "#0E103F" }} data-testid={`launch-post-revenue-${p.id}`}>
                            {p.revenue > 0 ? gbp(p.revenue) : "£0"}
                          </div>
                          <div className="text-[0.7rem] text-navy/55">{p.signups} signup{p.signups === 1 ? "" : "s"} · {p.visits} visits</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right: MRR movement */}
            <div>
              <h2 className="overline text-navy/65 mb-2.5 flex items-center gap-1.5"><TrendUp size={14} weight="fill" /> Revenue movement</h2>
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card-surface p-5" data-testid="launch-mrr-trend">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-heading font-bold text-navy text-lg">MRR trend</h3>
                  <span className="text-sm font-semibold text-positive" style={{ color: "#00A859" }}>{gbp(stats.mrr)}/mo</span>
                </div>
                <p className="text-xs text-navy/65 mb-3">New MRR (30d): <strong>{gbp(stats.new_mrr_30d)}</strong> · Signups (30d): <strong>{stats.signups_30d || 0}</strong></p>
                {chartData.length >= 2 ? (
                  <div className="h-[240px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="lFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#00A859" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="#00A859" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eef0f5" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#8a8a99" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: "#8a8a99" }} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => `£${v}`} />
                        <Tooltip formatter={(v) => [gbp(v), "MRR"]} contentStyle={{ borderRadius: 10, border: "1px solid #eef0f5", fontSize: 12 }} />
                        <Area type="monotone" dataKey="mrr" stroke="#00A859" strokeWidth={2.5} fill="url(#lFill)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[240px] flex flex-col items-center justify-center text-center">
                    <TrendUp size={28} className="mb-2 text-navy/25" />
                    <p className="text-sm text-navy/70 max-w-xs">The MRR trend fills in as daily snapshots accumulate. Signups from your campaign will show up here as revenue.</p>
                  </div>
                )}
              </motion.div>

              <div className="grid grid-cols-2 gap-3 mt-3">
                <Stat icon={UserPlus} label="New (30d)" value={stats.signups_30d || 0} sub="signups" color="#F37A24" />
                <Stat icon={Percent} label="Trial → Paid" value={`${stats.trial_conversion ?? 0}%`} sub="conversion" color="#0E103F" />
              </div>
            </div>
          </div>

          {/* Competitor watch — the /compare page auto-refreshes monthly */}
          {compare && (
            <div className="card-surface p-5 mt-4" data-testid="competitor-watch">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <h2 className="overline text-navy/65 flex items-center gap-1.5"><Scales size={14} weight="fill" /> Competitor watch · Compare page</h2>
                <div className="flex items-center gap-2">
                  <button onClick={refreshCompare} disabled={refreshing} data-testid="compare-refresh-btn"
                    className="inline-flex items-center gap-1.5 text-white text-sm font-semibold px-3.5 py-2 rounded-xl disabled:opacity-50" style={{ background: "#F37A24" }}>
                    <ArrowsClockwise size={15} weight="bold" className={refreshing ? "animate-spin" : ""} /> Check now
                  </button>
                  <button onClick={revertCompare} data-testid="compare-revert-btn"
                    className="inline-flex items-center gap-1.5 text-navy text-sm font-semibold px-3.5 py-2 rounded-xl border border-slate-200 hover:border-slate-300">
                    <ArrowCounterClockwise size={15} /> Revert to defaults
                  </button>
                </div>
              </div>
              <p className="text-sm text-navy/70 mb-3">
                Fetches each competitor's pricing page & Xero listing monthly, auto-publishes any price/feature
                changes and emails a summary to <b>{compare.notify_email || "Paul@workwithlottie.com"}</b>. Last verified{" "}
                <b>{compare.last_verified ? new Date(compare.last_verified).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—"}</b>.
              </p>

              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 mb-3" data-testid="compare-notify-email">
                <label className="block text-xs font-semibold text-navy/60 uppercase tracking-wide mb-1.5">Email the monthly summary to</label>
                <div className="flex flex-wrap items-center gap-2">
                  <input type="email" value={emailDraft} onChange={(e) => setEmailDraft(e.target.value)}
                    placeholder="you@practice.co.uk" data-testid="compare-notify-email-input"
                    className="flex-1 min-w-[220px] rounded-xl border border-slate-200 px-3 py-2 text-sm text-navy focus:border-orange focus:ring-1 focus:ring-orange outline-none" />
                  <button onClick={saveNotifyEmail} disabled={savingEmail || !emailDraft.trim() || emailDraft.trim() === (compare.notify_email || "")}
                    data-testid="compare-notify-email-save"
                    className="inline-flex items-center gap-1.5 text-navy text-sm font-semibold px-3.5 py-2 rounded-xl border border-slate-200 hover:border-orange hover:text-orangeink disabled:opacity-40 transition-colors">
                    {savingEmail ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>

              <label className="flex items-center gap-2 mb-3 text-sm text-navy/80 cursor-pointer" data-testid="compare-approval-toggle">
                <input type="checkbox" checked={!!compare.require_approval} onChange={(e) => toggleApproval(e.target.checked)}
                  className="rounded border-slate-300 text-[#F37A24] focus:ring-[#F37A24]" />
                Hold changes for my approval before they go live
              </label>

              {compare.pending && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5 mb-4" data-testid="compare-pending">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <div className="text-sm font-bold text-amber-900">
                      {compare.pending.changes.length} change{compare.pending.changes.length === 1 ? "" : "s"} awaiting your approval
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={approvePending} data-testid="compare-approve-btn"
                        className="inline-flex items-center gap-1.5 text-white text-xs font-semibold px-3 py-1.5 rounded-lg" style={{ background: "#00A859" }}>
                        Approve & publish
                      </button>
                      <button onClick={discardPending} data-testid="compare-discard-btn"
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-amber-300 text-amber-900 hover:bg-amber-100">
                        Discard
                      </button>
                    </div>
                  </div>
                  {compare.pending.changes.map((c, j) => (
                    <div key={j} className="text-sm text-amber-900/90" data-testid={`compare-pending-${j}`}>
                      <span className="font-semibold">{c.competitor}</span> · {c.label}:{" "}
                      <span className="opacity-50 line-through">{String(c.from)}</span>{" "}
                      <span className="font-semibold">→ {String(c.to)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="text-xs font-semibold text-navy/60 uppercase tracking-wide mb-2">Recent changes</div>
              {(compare.change_log || []).length === 0 ? (
                <p className="text-sm text-navy/60">No competitor changes published yet.</p>
              ) : (
                <div className="space-y-3" data-testid="compare-change-log">
                  {(compare.change_log || []).slice(0, 3).map((e, i) => (
                    <div key={i} className="border-l-2 border-orange/40 pl-3">
                      <div className="text-xs text-navy/50 mb-1">
                        {new Date(e.at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} · {e.count} change{e.count === 1 ? "" : "s"}
                      </div>
                      {e.changes.map((c, j) => (
                        <div key={j} className="text-sm text-navy/80" data-testid={`compare-change-${i}-${j}`}>
                          <span className="font-semibold">{c.competitor}</span> · {c.label}:{" "}
                          <span className="text-navy/45 line-through">{String(c.from)}</span>{" "}
                          <span className="text-positive font-semibold">→ {String(c.to)}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}
